/**
 * Utility functions for contract period calculations and formatting.
 * Handles exact integer Years, Months, and Weeks.
 * 
 * Rules:
 * 1. Inclusive of end date (boundary = end_date + 1 day).
 * 2. Day to week rounding:
 *    - If remaining days <= 3 days: round down to previous week (do not add week).
 *    - If remaining days > 3 days: round up to next week (+1 week).
 *    - Examples: 4 weeks, 5 days = 5 weeks; 4 weeks, 3 days = 4 weeks.
 * 3. Display format: "X year, Y month, Z week"
 *    - If year, month, or week is 0/empty, do NOT display that unit.
 *    - Examples: 
 *      1y, 5m, 3w -> "1 year, 5 month, 3 week"
 *      0y, 5m, 3w -> "5 month, 3 week"
 *      1y, 5m, 0w -> "1 year, 5 month"
 *      2y, 0m, 0w -> "2 year"
 *      0y, 0m, 4w -> "4 week"
 *      0y, 0m, 0w -> "0 week"
 */

/**
 * Calculates contract duration between two date strings (YYYY-MM-DD).
 * 
 * @param {string} startDate - Start date YYYY-MM-DD
 * @param {string} endDate - End date YYYY-MM-DD
 * @returns {{ years: number, months: number, weeks: number, totalMonths: number, totalWeeks: number }}
 */
export function calculateContractDuration(startDate, endDate) {
  if (!startDate || !endDate) {
    return { years: 0, months: 0, weeks: 0, totalMonths: 0, totalWeeks: 0 };
  }

  const [sY, sM, sD] = startDate.split('-').map(Number);
  const [eY, eM, eD] = endDate.split('-').map(Number);

  if (!sY || !eY || isNaN(sY) || isNaN(eY)) {
    return { years: 0, months: 0, weeks: 0, totalMonths: 0, totalWeeks: 0 };
  }

  const start = new Date(sY, sM - 1, sD);
  const end = new Date(eY, eM - 1, eD);

  if (end < start) {
    return { years: 0, months: 0, weeks: 0, totalMonths: 0, totalWeeks: 0 };
  }

  // Contract dates are inclusive: add 1 day to end date for boundary comparison
  const endBoundary = new Date(eY, eM - 1, eD + 1);

  let years = endBoundary.getFullYear() - sY;
  let months = endBoundary.getMonth() - (sM - 1);
  let days = endBoundary.getDate() - sD;

  if (days < 0) {
    months -= 1;
    const prevMonthDays = new Date(endBoundary.getFullYear(), endBoundary.getMonth(), 0).getDate();
    days += prevMonthDays;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  // Rule:
  // If remaining days <= 3 days, count as previous week (do not add a week).
  // If remaining days > 3 days, add 1 week (+1 week).
  const fullWeeks = Math.floor(days / 7);
  const remDays = days % 7;
  let weeks = fullWeeks + (remDays > 3 ? 1 : 0);

  const totalMonths = years * 12 + months;
  const totalWeeks = totalMonths * 4 + weeks;

  // Total duration eliminates years: represented only in months and weeks
  return { 
    years: 0, 
    months: totalMonths, 
    weeks, 
    totalMonths, 
    totalWeeks 
  };
}

/**
 * Calculates option period given duration and base period.
 * Option Period = Duration - Base Period.
 * 
 * Supports both object input ({ years, months, weeks }) and numeric totalMonths.
 * 
 * @param {object|number} duration 
 * @param {object|number} basePeriod 
 * @returns {{ years: number, months: number, weeks: number, totalWeeks: number }}
 */
export function calculateOptionPeriod(duration, basePeriod) {
  let dY = 0, dM = 0, dW = 0;
  let bY = 0, bM = 0, bW = 0;

  if (typeof duration === 'object' && duration !== null) {
    dY = parseInt(duration.years) || 0;
    dM = parseInt(duration.months) || 0;
    dW = parseInt(duration.weeks) || 0;
  } else {
    const num = parseInt(duration) || 0;
    dY = Math.floor(num / 12);
    dM = num % 12;
    dW = 0;
  }

  if (typeof basePeriod === 'object' && basePeriod !== null) {
    bY = parseInt(basePeriod.years) || 0;
    bM = parseInt(basePeriod.months) || 0;
    bW = parseInt(basePeriod.weeks) || 0;
  } else {
    const num = parseInt(basePeriod) || 0;
    bY = Math.floor(num / 12);
    bM = num % 12;
    bW = 0;
  }

  const dTotalMonths = dY * 12 + dM;
  const bTotalMonths = bY * 12 + bM;

  const durTotalWeeks = dTotalMonths * 4 + dW;
  const baseTotalWeeks = bTotalMonths * 4 + bW;

  if (baseTotalWeeks > durTotalWeeks || durTotalWeeks <= 0) {
    return { years: 0, months: 0, weeks: 0, totalWeeks: 0 };
  }

  let remWeeks = dW - bW;
  let remTotalMonths = dTotalMonths - bTotalMonths;

  if (remWeeks < 0) {
    remTotalMonths -= 1;
    remWeeks += 4;
  }

  if (remTotalMonths < 0) {
    return { years: 0, months: 0, weeks: 0, totalWeeks: 0 };
  }

  let remYears = 0;
  let remMonths = remTotalMonths;

  if (dY > 0) {
    remYears = Math.floor(remTotalMonths / 12);
    remMonths = remTotalMonths % 12;
  }

  return { 
    years: remYears, 
    months: remMonths, 
    weeks: remWeeks, 
    totalWeeks: durTotalWeeks - baseTotalWeeks 
  };
}

/**
 * Formats duration specifically without years (months and weeks only).
 * 
 * @param {any} duration 
 * @returns {string}
 */
export function formatDuration(duration) {
  if (!duration) return '0 week';
  const parsed = parsePeriodValue(duration);
  return formatPeriod(0, parsed.totalMonths, parsed.weeks);
}

/**
 * Formats a period into a human-readable string: "X year, Y month, Z week".
 * If year, month, or week is 0/empty, that unit is omitted.
 * If all units are 0, returns "0 week".
 * 
 * @param {number} years 
 * @param {number} months 
 * @param {number} [weeks] 
 * @returns {string}
 */
export function formatPeriod(years, months, weeks) {
  const y = parseInt(years) || 0;
  const m = parseInt(months) || 0;
  const w = parseInt(weeks) || 0;

  const parts = [];
  if (y > 0) parts.push(`${y} year`);
  if (m > 0) parts.push(`${m} month`);
  if (w > 0) parts.push(`${w} week`);

  if (parts.length === 0) {
    return '0 week';
  }

  return parts.join(', ');
}

/**
 * Formats total months into year and month text.
 * 
 * @param {number} totalMonths 
 * @returns {string}
 */
export function formatPeriodFromMonths(totalMonths) {
  const t = parseInt(totalMonths) || 0;
  const years = Math.floor(t / 12);
  const months = t % 12;
  return formatPeriod(years, months, 0);
}

/**
 * Parses any incoming period value (formatted string, object, or legacy float)
 * into a standardized { years, months, weeks, totalMonths, totalWeeks } structure.
 * 
 * @param {any} val - e.g. "1 year, 5 month, 3 week", "1 Year 6 Months", { years: 1, months: 5, weeks: 3 }
 * @returns {{ years: number, months: number, weeks: number, totalMonths: number, totalWeeks: number }}
 */
export function parsePeriodValue(val) {
  if (val === undefined || val === null || val === '') {
    return { years: 0, months: 0, weeks: 0, totalMonths: 0, totalWeeks: 0 };
  }

  if (typeof val === 'object') {
    const y = parseInt(val.years) || 0;
    const m = parseInt(val.months) || 0;
    const w = parseInt(val.weeks) || 0;
    return { 
      years: y, 
      months: m, 
      weeks: w, 
      totalMonths: y * 12 + m, 
      totalWeeks: (y * 12 + m) * 4 + w 
    };
  }

  if (typeof val === 'string') {
    const yearMatch = val.match(/(\d+)\s*(?:year|yr|thn|tahun)/i);
    const monthMatch = val.match(/(\d+)\s*(?:month|mo|bln|bulan)/i);
    const weekMatch = val.match(/(\d+)\s*(?:week|wk|mg|minggu)/i);

    if (yearMatch || monthMatch || weekMatch) {
      const y = yearMatch ? parseInt(yearMatch[1]) : 0;
      const m = monthMatch ? parseInt(monthMatch[1]) : 0;
      const w = weekMatch ? parseInt(weekMatch[1]) : 0;
      return { 
        years: y, 
        months: m, 
        weeks: w, 
        totalMonths: y * 12 + m, 
        totalWeeks: (y * 12 + m) * 4 + w 
      };
    }
  }

  const num = parseFloat(val);
  if (isNaN(num) || num <= 0) {
    return { years: 0, months: 0, weeks: 0, totalMonths: 0, totalWeeks: 0 };
  }

  // Decimal years conversion (e.g. 1.5 -> 1 year 6 months)
  const y = Math.floor(num);
  const frac = num - y;
  let m = Math.round(frac * 12);
  let finalY = y;

  if (m >= 12) {
    finalY += 1;
    m -= 12;
  }

  return { 
    years: finalY, 
    months: m, 
    weeks: 0, 
    totalMonths: finalY * 12 + m, 
    totalWeeks: (finalY * 12 + m) * 4 
  };
}

/**
 * Display helper for contract tables and summaries.
 * Safely handles strings, legacy float numbers, and numeric fields.
 * 
 * @param {any} rawVal 
 * @param {number} [years] 
 * @param {number} [months] 
 * @param {number} [weeks] 
 * @returns {string}
 */
export function displayPeriod(rawVal, years, months, weeks) {
  if (years !== undefined && months !== undefined && years !== null && months !== null) {
    return formatPeriod(years, months, weeks || 0);
  }
  const parsed = parsePeriodValue(rawVal);
  return formatPeriod(parsed.years, parsed.months, parsed.weeks);
}
